<template>
  <OwnCard
    class="own-table-table-renderer"
    el="table"
    :border-color="border ? undefined : 'transparent'"
    :control="border"
    :style="{
      ...(seamlessTop && {
        marginTop: `-${seamlessOffset}px`,
        paddingTop: '0',
        borderTop: 'none',
        borderTopLeftRadius: '0',
        borderTopRightRadius: '0',
      }),
      ...(seamlessBottom && {
        marginBottom: `-${seamlessOffset}px`,
        paddingBottom: '0',
        borderBottom: 'none',
        borderBottomLeftRadius: '0',
        borderBottomRightRadius: '0',
      }),
      ...(variant === 'settings-slim' && {
        borderRadius: '0',
      }),
    }"
  >
    <!-- Title slot -->
    <div v-if="$slots.title" class="own-table-table-renderer__title">
      <slot name="title" />
    </div>

    <thead
      v-if="!hideHeading || $slots['sub-heading']"
      class="own-table-table-renderer__heading-container"
    >
      <tr
        v-if="!hideHeading"
        :class="[
          'own-table-table-renderer__heading',
          `flex-row gap-${options.row.spacing} align-center`,
        ]"
      >
        <span
          v-if="reorderable"
          class="own-table-table-renderer__reorder-heading-spacer"
        ></span>

        <th
          v-for="column of props.columns"
          :key="column.key"
          class="flex-1"
          :class="[`text--${column?.align ?? 'left'}`]"
          :style="{
            maxWidth: column.minWidth ? undefined : toPx(column.width),
            minWidth: toPx(column.minWidth ?? column.width),
          }"
        >
          <slot :name="`heading.${column.key}`">
            <OwnType
              v-if="!column?.hideLabel"
              class="w-full"
              el="span"
              :text="column?.label ?? '—'"
              :info="column?.info"
              :variant="headerVariant"
            />
          </slot>
        </th>
      </tr>

      <!-- Sub-heading slot -->
      <tr
        v-if="$slots['sub-heading']"
        class="own-table-table-renderer__sub-heading-row"
      >
        <th class="own-table-table-renderer__sub-heading-cell">
          <div class="own-table-table-renderer__sub-heading-content">
            <slot name="sub-heading" />
          </div>
        </th>
      </tr>
    </thead>

    <!--  Search returns no results  -->
    <div v-if="!hasItems" class="own-table-table-renderer__no-search-results">
      <template v-if="$slots['no-query-results']">
        <slot name="no-query-results" />
      </template>

      <template v-else-if="$slots['no-data']">
        <slot name="no-data" />
      </template>
    </div>

    <tbody
      v-else
      ref="reorderableList"
      class="own-table-table-renderer__body flex-col"
    >
      <!-- @vue-expect-error key should be part of item -->
      <tr
        v-for="(item, rowIdx) of items"
        :key="item[itemKey]"
        :class="[
          'own-table-table-renderer__row',
          hasClick && 'own-table-table-renderer__row--click',
          options.body.dividers && 'own-table-table-renderer__row--dividers',
          `flex-row gap-${options.row.spacing} align-center`,
        ]"
        @keyup.enter="emit('click', item)"
        @click="emit('click', item)"
      >
        <DragHandle v-if="props.reorderable" />

        <td
          v-for="(column, idx) of props.columns"
          :key="`${column.key}-${item[itemKey]}`"
          class="flex-1"
          :style="{
            maxWidth: column.minWidth ? undefined : toPx(column.width),
            minWidth: toPx(column.minWidth ?? column.width),
          }"
        >
          <!--  Override specific column (#item.name)  -->
          <slot
            :name="`item.${column.key}`"
            :item="item"
            :row-idx="rowIdx"
            :is-first="idx === 0"
          >
            <!--  Override all columns (#item)  -->
            <slot
              :column-key="column.key"
              name="item"
              :item="item"
              :is-first="idx === 0"
            >
              <DefaultTableCell :item="item" :column="column" />
            </slot>
          </slot>
        </td>
      </tr>
    </tbody>

    <!-- Footer slot -->
    <div v-if="$slots.footer" class="own-table-table-renderer__footer">
      <slot name="footer" />
    </div>
  </OwnCard>
</template>

<script lang="ts" setup generic="T extends Record<string, unknown>">
import Sortable, { SortableEvent } from 'sortablejs'
import { computed, nextTick, onMounted, ref, watch } from 'vue'

import { ReorderEvent } from '@/utils/helpers/reorder'

import { OwnCard } from '../../OwnCard'
import { OwnType } from '../../OwnType'
import DragHandle from '../../reorderable/DragHandle.vue'
import { OwnTableColumnConfig, OwnTableVariants } from '../types'
import { getTableOptions } from '../utils/getTableOptions'

import DefaultTableCell from './DefaultTableCell.vue'

const props = withDefaults(
  defineProps<{
    border: boolean
    columns: OwnTableColumnConfig<T>[]
    hasClick: boolean
    hideHeading: boolean
    items: T[]
    itemKey: string
    query?: string
    reorderable: boolean
    seamlessBottom?: boolean
    seamlessOffset?: number
    seamlessTop?: boolean
    variant: OwnTableVariants
  }>(),
  {
    hideHeading: false,
    query: undefined,
    seamlessBottom: false,
    seamlessOffset: 20,
    seamlessTop: false,
  }
)

const emit = defineEmits<{
  (event: 'click', value: T): void
  (event: 'reorder', value: ReorderEvent): void
}>()

const reorderableList = ref<HTMLTableSectionElement | null>(null)

const options = getTableOptions(props.variant)

const hasItems = computed(() => {
  return props.items.length > 0
})

const toPx = (value: number | undefined) => {
  if (value === undefined) return undefined

  return `${value}px`
}

const rowRadius = computed(() => toPx(options.row.radius ?? 0))
const rowPaddingTopBottom = computed(() =>
  toPx(options.row.padding?.topBottom ?? 16)
)
const rowPaddingRightLeft = computed(() =>
  toPx(options.row.padding?.rightLeft ?? 16)
)
const bodyPaddingTopBottom = computed(() =>
  toPx(options.body.padding?.topBottom ?? 0)
)
const bodyPaddingRightLeft = computed(() =>
  toPx(options.body.padding?.rightLeft ?? 0)
)
const headerPaddingTopBottom = computed(() =>
  toPx(options.header.padding?.topBottom ?? 16)
)
const headerPaddingRightLeft = computed(() =>
  toPx(options.header.padding?.rightLeft ?? 0)
)
const headerVariant = computed(() => options.header.variant ?? 'title-regular')

const subHeadingPadding = computed(() => {
  const basePadding = 32
  const gapSpacing = options.row.spacing
  const reorderOffset = props.reorderable ? 24 + gapSpacing : 0

  return `${basePadding + gapSpacing + reorderOffset}px`
})

const headingContainerPadding = computed(() => {
  // Only apply top padding if the main heading is shown
  if (props.hideHeading) {
    return `0 ${headerPaddingRightLeft.value} 0 ${headerPaddingRightLeft.value}`
  } else {
    return `${headerPaddingTopBottom.value} ${headerPaddingRightLeft.value} 0 ${headerPaddingRightLeft.value}`
  }
})

const mountSortable = () => {
  nextTick(() => {
    if (props.reorderable && reorderableList.value) {
      Sortable.create(reorderableList.value, {
        animation: 150,
        direction: 'vertical',
        handle: '.drag-handle',
        onEnd: ({ oldIndex, newIndex }: SortableEvent) => {
          if (oldIndex === newIndex) return
          emit('reorder', { newIndex, oldIndex } as ReorderEvent)
        },
      })
    }
  })
}

onMounted(mountSortable)

watch(hasItems, (newValue) => {
  if (newValue) mountSortable()
})
</script>

<style lang="scss">
.own-table-table-renderer {
  overflow: auto;

  &__title {
    padding: 16px 16px 0 16px;
  }

  &__footer {
    padding: 0 16px 16px 16px;
  }

  &__heading-container {
    // The header is a stretched flex item sized to the card, not to its cells.
    // When the columns overflow horizontally, let it grow to its content so the
    // heading divider and the percentage-width sub-heading both span every
    // column instead of stopping at the card edge.
    min-width: max-content;
    padding: v-bind(headingContainerPadding);
  }

  &__reorder-heading-spacer {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }

  &__no-search-results {
    padding: 16px;
  }

  &__heading {
    padding-bottom: v-bind(headerPaddingTopBottom);
    border-bottom: 1px dashed var(--divider);
  }

  &__sub-heading-row {
    background-color: var(--background-secondary);
    border-bottom: 1px solid var(--divider);
    display: block;
    width: calc(100% + 32px);
    margin-left: -16px;
    margin-right: -16px;
  }

  &__sub-heading-cell {
    padding: 0;
    background-color: var(--background-secondary);
    width: 100%;
    text-align: left;
    font-weight: normal;
    display: block;
  }

  &__sub-heading-content {
    padding: 12px 16px 12px v-bind(subHeadingPadding);
  }

  &__body {
    padding: v-bind(bodyPaddingTopBottom) v-bind(bodyPaddingRightLeft);
  }

  &__row {
    border-radius: v-bind(rowRadius);
    padding: v-bind(rowPaddingTopBottom) v-bind(rowPaddingRightLeft);

    &--click {
      cursor: pointer;

      &:hover {
        background-color: var(--background-active);
      }
    }

    &--dividers {
      position: relative;

      &:after {
        content: '';
        position: absolute;
        bottom: 0;
        left: v-bind(headerPaddingRightLeft);
        background: var(--divider);
        height: 1px;
        width: calc(100% - v-bind(headerPaddingRightLeft) * 2);
      }
    }

    &:last-child {
      &:after {
        height: 0;
      }
    }
  }

  // Seamless styles now handled via inline styles for better specificity
}
</style>
